import React from "react";
import {
  Bath,
  Bed,
  Building,
  Calendar,
  ExternalLink,
  Home,
  MapPin,
  Ruler,
  User,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Property from "@/model/interface/Property";

interface PropertyDetailCardProps {
  property: Property;
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  SINGLE_FAMILY: "Single Family",
  MULTI_FAMILY: "Multi Family",
  CONDO: "Condo",
  TOWNHOUSE: "Townhouse",
  MOBILE_HOME: "Mobile Home",
  LAND: "Land",
  COMMERCIAL: "Commercial",
  INDUSTRIAL: "Industrial",
  MIXED_USE: "Mixed Use",
  OTHER: "Other",
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  OWNER_OCCUPIED: "Owner Occupied",
  ABSENTEE: "Absentee",
  CORPORATE: "Corporate",
  TRUST: "Trust",
  UNKNOWN: "Unknown",
};

const formatCurrency = (value?: number) => {
  if (value == null) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value?: number) => {
  if (value == null) return null;
  return new Intl.NumberFormat("en-US").format(value);
};

export function PropertyDetailCard({ property }: PropertyDetailCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Home className="h-4 w-4" />
            Property Details
          </CardTitle>
          {property.propertyType && (
            <Badge variant="outline">
              {PROPERTY_TYPE_LABELS[property.propertyType] || property.propertyType}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm font-medium">{property.address}</p>
            {property.city && (
              <p className="text-xs text-muted-foreground">
                {property.city}{property.state ? `, ${property.state}` : ""} {property.zip || ""}
                {property.county ? ` · ${property.county} County` : ""}
              </p>
            )}
          </div>
        </div>

        {property.listingUrl && (
          <a
            href={property.listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            View original listing
          </a>
        )}

        {(property.beds != null || property.baths != null || property.sqft != null) && (
          <div className="flex items-center gap-4 text-sm">
            {property.beds != null && (
              <div className="flex items-center gap-1">
                <Bed className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{property.beds} bed</span>
              </div>
            )}
            {property.baths != null && (
              <div className="flex items-center gap-1">
                <Bath className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{property.baths} bath</span>
              </div>
            )}
            {property.sqft != null && (
              <div className="flex items-center gap-1">
                <Ruler className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{formatNumber(property.sqft)} sqft</span>
              </div>
            )}
          </div>
        )}

        {(property.lotSizeSqft != null || property.yearBuilt != null) && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {property.lotSizeSqft != null && (
              <span>Lot: {formatNumber(property.lotSizeSqft)} sqft</span>
            )}
            {property.yearBuilt != null && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>Built {property.yearBuilt}</span>
              </div>
            )}
          </div>
        )}

        {(property.taxAssessedValue != null || property.marketValue != null || property.equityEstimate != null || property.mortgageBalance != null) && (
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            {property.marketValue != null && (
              <div>
                <p className="text-xs text-muted-foreground">Market Value</p>
                <p className="text-sm font-medium">{formatCurrency(property.marketValue)}</p>
              </div>
            )}
            {property.taxAssessedValue != null && (
              <div>
                <p className="text-xs text-muted-foreground">Tax Assessed</p>
                <p className="text-sm font-medium">{formatCurrency(property.taxAssessedValue)}</p>
              </div>
            )}
            {property.equityEstimate != null && (
              <div>
                <p className="text-xs text-muted-foreground">Equity</p>
                <p className="text-sm font-medium text-green-600">{formatCurrency(property.equityEstimate)}</p>
              </div>
            )}
            {property.mortgageBalance != null && (
              <div>
                <p className="text-xs text-muted-foreground">Mortgage</p>
                <p className="text-sm font-medium">{formatCurrency(property.mortgageBalance)}</p>
              </div>
            )}
          </div>
        )}

        {(property.ownerName || property.ownerType) && (
          <div className="flex items-center gap-2 pt-2 border-t text-sm">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{property.ownerName || "Unknown"}</span>
            {property.ownerType && property.ownerType !== "UNKNOWN" && (
              <Badge variant="secondary" className="text-xs">
                {OWNER_TYPE_LABELS[property.ownerType] || property.ownerType}
              </Badge>
            )}
          </div>
        )}

        {property.zoning && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building className="h-3.5 w-3.5" />
            <span>Zoning: {property.zoning}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
